import type { Reference } from "src/db";
import type { FeedbackSchema } from "../MetadataFeedback/types";

const safeDecodeUriComponent = (value: unknown): string => {
    if (typeof value !== "string") {
        return "";
    }

    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

const mapToReference = (item: unknown, fallbackIndex: number): Reference | undefined => {
    if (!item || typeof item !== "object") {
        return undefined;
    }

    const candidate = item as Record<string, unknown>;
    const resolvedIndex = fallbackIndex + 1;
    const rawReferenceId = candidate.reference_id ?? candidate.referenceId;
    const title = typeof candidate.title === "string" ? candidate.title : "";
    const source = safeDecodeUriComponent(candidate.source);
    const dataSourceType =
        candidate.dataSourceType ?? candidate.data_source_type ?? candidate.datasource_type;

    let page_numbers: Set<number>;
    if (candidate.page_numbers instanceof Set) {
        // Ensure all elements are numbers
        const values = [...candidate.page_numbers];
        if (values.every((v) => typeof v === "number")) {
            page_numbers = candidate.page_numbers as Set<number>;
        } else {
            page_numbers = new Set();
        }
    } else if (typeof candidate.page_number === "number") {
        page_numbers = new Set([candidate.page_number]);
    } else {
        page_numbers = new Set();
    }
    const normalizedReferenceId =
        typeof rawReferenceId === "string"
            ? rawReferenceId
            : typeof rawReferenceId === "number"
              ? String(rawReferenceId)
              : String(resolvedIndex);

    if (!normalizedReferenceId) {
        return undefined;
    }

    return {
        reference_id: normalizedReferenceId,
        title,
        source,
        dataSourceType: typeof dataSourceType === "string" ? dataSourceType : "",
        page_numbers,
    } satisfies Reference;
};

export const normalizeReferences = (documents: unknown): Reference[] | undefined => {
    if (!Array.isArray(documents) || documents.length === 0) {
        return undefined;
    }

    const normalized = documents
        .map((item, index) => mapToReference(item, index))
        .filter((item): item is Reference => Boolean(item));

    return normalized.length > 0 ? normalized : undefined;
};

const extractFallbackDocuments = (rowData: FeedbackSchema | null) => {
    const source = rowData?._source as Record<string, unknown> | undefined;
    if (!source) {
        return undefined;
    }

    return source["cts_answer.documents"];
};

export const extractReferencesFromFeedback = (
    rowData: FeedbackSchema | null,
): Reference[] | undefined => {
    const primaryDocuments = rowData?._source?.cts_answer?.documents;
    if (primaryDocuments && primaryDocuments.length > 0) {
        return normalizeReferences(primaryDocuments);
    }

    const fallbackDocuments = extractFallbackDocuments(rowData);
    return normalizeReferences(fallbackDocuments);
};
